/**
 * Team Page
 *
 * Admin Dashboard user management — invite, disable, delete and (for admins)
 * change roles of other dashboard users.
 *
 * Members get read-only access; managers can act on members only;
 * admins can act on anyone. See `assertCanActOn` on the backend for the
 * authoritative rule set.
 */

import { useEffect, useMemo, useState } from 'react';
import { Search, UserPlus } from 'lucide-react';

import { TeamTable } from '@/components/team/team-table';
import { InviteTeamUserDialog } from '@/components/team/invite-team-user-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { toast } from '@/hooks/use-toast';
import {
  useDeleteTeamUser,
  useDisableTeamUser,
  useEnableTeamUser,
  useResendTeamInvite,
  useTeamUsers,
  useUpdateTeamUserRole,
} from '@/hooks/use-team';
import { useAuth } from '@/state/auth-context';
import type { TeamRole, TeamUser } from '@/types/team';

type PendingAction =
  | { kind: 'disable'; user: TeamUser }
  | { kind: 'delete'; user: TeamUser }
  | { kind: 'role'; user: TeamUser; nextRole: TeamRole };

function describePending(action: PendingAction): { title: string; body: string; cta: string } {
  switch (action.kind) {
    case 'disable':
      return {
        title: `Disable ${action.user.email}?`,
        body: 'They will be signed out and unable to access the dashboard until re-enabled.',
        cta: 'Disable',
      };
    case 'delete':
      return {
        title: `Delete ${action.user.email}?`,
        body: 'This permanently removes the user from Cognito and cannot be undone.',
        cta: 'Delete',
      };
    case 'role':
      return {
        title: `Set ${action.user.email} as ${action.nextRole}?`,
        body: `Their permissions will change immediately. The role will take effect the next time their token refreshes.`,
        cta: 'Update role',
      };
  }
}

export function Component() {
  const { state } = useAuth();
  const callerRole: TeamRole = state.user?.role ?? 'admin';
  const callerUserId = state.user?.userId ?? null;
  const canInvite = callerRole === 'admin' || callerRole === 'manager';

  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [inviteOpen, setInviteOpen] = useState(false);
  const [pending, setPending] = useState<PendingAction | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const params = useMemo(
    () => ({ search: debouncedSearch || undefined, limit: 50 }),
    [debouncedSearch]
  );

  const { data, isLoading, error, refetch } = useTeamUsers(params);
  const users = data?.data ?? [];

  const resend = useResendTeamInvite();
  const disable = useDisableTeamUser();
  const enable = useEnableTeamUser();
  const remove = useDeleteTeamUser();
  const updateRole = useUpdateTeamUserRole();

  const handleResend = async (user: TeamUser) => {
    try {
      await resend.mutateAsync(user.username);
      toast({ title: 'Invite resent', description: `A new invitation was sent to ${user.email}.` });
    } catch (e) {
      toast({
        title: 'Failed to resend invite',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const handleEnable = async (user: TeamUser) => {
    try {
      await enable.mutateAsync(user.username);
      toast({ title: 'User enabled', description: `${user.email} can sign in again.` });
    } catch (e) {
      toast({
        title: 'Failed to enable user',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  const confirmPending = async () => {
    if (!pending) return;
    try {
      if (pending.kind === 'disable') {
        await disable.mutateAsync(pending.user.username);
        toast({ title: 'User disabled', description: `${pending.user.email} was disabled.` });
      } else if (pending.kind === 'delete') {
        await remove.mutateAsync(pending.user.username);
        toast({ title: 'User deleted', description: `${pending.user.email} was removed.` });
      } else {
        await updateRole.mutateAsync({
          username: pending.user.username,
          body: { role: pending.nextRole },
        });
        toast({
          title: 'Role updated',
          description: `${pending.user.email} is now a ${pending.nextRole}.`,
        });
      }
      setPending(null);
    } catch (e) {
      toast({
        title: 'Action failed',
        description: e instanceof Error ? e.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Team</h1>
          <p className="mt-2 text-muted-foreground">
            Manage dashboard users. {callerRole === 'member' && 'View-only access.'}
          </p>
        </div>
        {canInvite && (
          <Button onClick={() => setInviteOpen(true)} className="gap-2">
            <UserPlus className="h-4 w-4" />
            Invite member
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Team members</CardTitle>
          <CardDescription>
            {users.length} {users.length === 1 ? 'user' : 'users'}
            {debouncedSearch && ` · filtered by "${debouncedSearch}"`}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="search"
              placeholder="Search by email…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              className="pl-10"
            />
          </div>

          <TeamTable
            users={users}
            callerRole={callerRole}
            callerUserId={callerUserId}
            isLoading={isLoading}
            error={error instanceof Error ? error : null}
            onRetry={refetch}
            onResend={handleResend}
            onDisable={user => setPending({ kind: 'disable', user })}
            onEnable={handleEnable}
            onDelete={user => setPending({ kind: 'delete', user })}
            onChangeRole={(user, nextRole) =>
              setPending({ kind: 'role', user, nextRole })
            }
          />
        </CardContent>
      </Card>

      <InviteTeamUserDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        callerRole={callerRole}
        onInvited={email =>
          toast({ title: 'Invitation sent', description: `An invite was emailed to ${email}.` })
        }
      />

      <AlertDialog open={pending !== null} onOpenChange={open => !open && setPending(null)}>
        <AlertDialogContent>
          {pending && (
            <>
              <AlertDialogHeader>
                <AlertDialogTitle>{describePending(pending).title}</AlertDialogTitle>
                <AlertDialogDescription>{describePending(pending).body}</AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={confirmPending}>
                  {describePending(pending).cta}
                </AlertDialogAction>
              </AlertDialogFooter>
            </>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

Component.displayName = 'TeamPage';
